import {
  Badge,
  HStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import React from 'react';
import {
  NoData,
  ScrollBarWithFade,
} from '@payler/ui-components';
import { TableSkeleton } from '../../components/TableSkeleton/TableSkeleton';
import { useDayjsFormat } from '@payler/utils';
import { TableFooter } from '../components/TableFooter';
import { tableStyleConfig } from '../tableStyleConfig';
import { GroupRow } from '../PaymentsTable/GroupRow';
import { AmountWithCurrencyCell } from '../PaymentsTable/AmountWithCurrencyCell';
import {
  useApmPaymentsQuery,
  useApmPaymentsQueryPagerProps,
} from '../../hooks/apm-payments/queries';
import { TApmPayment, TApmPaymentStatus } from '../../../../../api/src';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useToasts } from '../../hooks/use-toasts';
import { ApmPaymentTooltip } from '../../components/ApmPaymentTooltip/ApmPaymentTooltip';
import { getApmSessionStatusBadgeVariant } from '../getApmSessionStatusBadgeVariant';

export const ApmPaymentsTable = () => {
  const props = useApmPaymentsQueryPagerProps();
  return (
    <>
      <React.Suspense fallback={null}>
        <TableWithData />
      </React.Suspense>
      <React.Suspense fallback={null}>
        <TableFooter paginationProps={props} />
      </React.Suspense>
    </>
  );
};

const TableWithData = () => {
  const { status, data } = useApmPaymentsQuery();
  const toasts = useToasts();

  switch (status) {
    case 'pending':
      return <TableSkeleton />;
    case 'error':
      toasts.error();
      return <NoData />;
    case 'success':
    default: {
      const empty = data.items.length === 0;
      if (empty) {
        return <NoData />;
      }
      return (
        <ScrollBarWithFade>
          <TableContainer position="relative">
            <Table styleConfig={tableStyleConfig}>
              <Header />
              <Tbody>
                {Object.entries(data.grouped)
                  .sort(([a], [b]) => -a.localeCompare(b))
                  .map(([group, rows]) => (
                    <Rows key={group} group={group} payments={rows} />
                  ))}
              </Tbody>
            </Table>
          </TableContainer>
        </ScrollBarWithFade>
      );
    }
  }
};

const Rows = ({
  group,
  payments,
}: {
  group: string;
  payments: TApmPayment[];
}) => {
  const format = useDayjsFormat();
  return (
    <>
      <tr>
        <GroupRow colSpan={11}>{format(group, 'dddd DD MMMM')}</GroupRow>
      </tr>
      {payments.map((row) => (
        <Row data={row} key={row.sessionId} />
      ))}
    </>
  );
};

const Row = ({ data }: { data: TApmPayment }) => {
  const navigate = useNavigate();
  const handleRowClick = () => {
    navigate(data.sessionId);
  };

  return (
    <Tr onClick={handleRowClick}>
      <Td>{data.sessionId ?? '-'}</Td>
      <Td>{data.orderId ?? '-'}</Td>
      <Td>{data.clientId ?? '-'}</Td>
      <Td>
        {data.paymentType ? (
          <Badge variant="grey">{data.paymentType}</Badge>
        ) : (
          '-'
        )}
      </Td>
      <Td>
        {data.paymentMethod ? (
          <Badge variant="grey">{data.paymentMethod}</Badge>
        ) : (
          '-'
        )}
      </Td>
      <Td>
        <StatusCell
          status={data.sessionStatus}
          description={data.errorDescription}
        />
      </Td>
      <Td textAlign="right">
        <AmountWithCurrencyCell
          amount={data.amount ?? 0}
          currencyCode={data.currency}
          amountOfRefunds={0}
        />
      </Td>
      <Td textAlign="right">
        <AmountWithCurrencyCell
          amount={data.settledAmount ?? data.amount}
          currencyCode={data.currency}
          amountOfRefunds={0}
        />
      </Td>
      <Td>{data.currency}</Td>
      <Td>{dayjs(data.createdAt).format('HH:mm:ss DD.MM.YYYY')}</Td>
      <Td>
        {data.closedAt
          ? dayjs(data.closedAt).format('HH:mm:ss DD.MM.YYYY')
          : '-'}
      </Td>
    </Tr>
  );
};

const Header = () => {
  const { t } = useTranslation(['tableHeader']);
  return (
    <Thead>
      <Tr>
        <Th>{t('tableHeader:sessionId')}</Th>
        <Th>{t('tableHeader:orderId')}</Th>
        <Th>{t('tableHeader:clientId')}</Th>
        <Th>{t('tableHeader:paymentType')}</Th>
        <Th>{t('tableHeader:paymentMethod')}</Th>
        <Th>{t('tableHeader:status')}</Th>
        <Th>{t('tableHeader:amount')}</Th>
        <Th>{t('tableHeader:paidAmount')}</Th>
        <Th>{t('tableHeader:currency')}</Th>
        <Th>{t('tableHeader:created')}</Th>
        <Th>{t('tableHeader:paid')}</Th>
      </Tr>
    </Thead>
  );
};

const StatusCell = ({
  status,
  description,
}: {
  status: TApmPaymentStatus;
  description?: string;
}) => {
  const badgeVariant = getApmSessionStatusBadgeVariant(status);
  return (
    <Badge variant={badgeVariant}>
      <HStack spacing={1}>
        <Text>{status ?? '-'}</Text>
        {description && <ApmPaymentTooltip label={description} />}
      </HStack>
    </Badge>
  );
};

export default ApmPaymentsTable;
